<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { isEqual } from '@travelata-libs/utils';
  import { onBeforeUnmount, onMounted, ref, toRefs, watch } from 'vue';

  import type { TouristsSelection } from '../model/types';
  import { useTouristsRooms } from '../model/useTouristsRooms';
  import TouristsRoomsDropdown from './TouristsRoomsDropdown.vue';

  interface TouristsRoomsFieldProps {
    modelValue?: TouristsSelection | null;
    isVisibleAddButton?: boolean; 
  }

  const props = withDefaults(defineProps<TouristsRoomsFieldProps>(), {
    modelValue: null,
    isVisibleAddButton: true,
  });


  interface TouristsRoomsFieldEmits {
    (e: 'update:modelValue', sel: TouristsSelection): void;
    (e: 'change', sel: TouristsSelection): void;
  }

  const emit = defineEmits<TouristsRoomsFieldEmits>();

  const core = useTouristsRooms({ initial: props.modelValue, isVisibleAddButton: props.isVisibleAddButton });

  const rootRef = ref<HTMLElement | null>(null);

  const onToggle = (): void => {
    core.toggle();
  };

  const onOutsidePointer = (event: MouseEvent): void => {
    const root = rootRef.value;
    const target = event.target as Node | null;
    if (root && target && !root.contains(target)) {
      core.close();
    }
  };

  watch(core.controlActivated, (active) => {
    if (active) {
      document.addEventListener('mousedown', onOutsidePointer);
    } else {
      document.removeEventListener('mousedown', onOutsidePointer);
    }
  });

  onBeforeUnmount(() => {
    document.removeEventListener('mousedown', onOutsidePointer);
  });

  watch(
    core.selection,
    (sel) => {
      emit('update:modelValue', sel);
      emit('change', sel);
    },
    { deep: true },
  );

  watch(
    () => props.modelValue,
    (next) => {
      if (!next) {
        return;
      }
      if (!isEqual(next, core.selection.value)) {
        core.restore(next);
      }
    },
    { deep: true },
  );

  onMounted(() => {
    core.restore(props.modelValue);
  });

  defineExpose({
    validate: (): boolean => core.validate(),
  });
</script>

<template>
  <div ref="rootRef" class="tourists-rooms-field" data-testid="tourists-rooms-field">
    <TouristsRoomsDropdown
      :rooms="core.rooms.value"
      :visible="core.controlActivated.value"
      :age-options="core.ageOptions"
      :can-add-room="core.canAddRoom.value"
      :can-remove-room="core.canRemoveRoom.value"
      :is-visible-header="isVisibleAddButton"
      :is-visible-add-button="isVisibleAddButton"
      @set-adults="core.setAdults($event.roomIndex, $event.value)"
      @add-child="core.addChild($event.roomIndex, $event.age)"
      @change-child-age="core.setChildAge($event.roomIndex, $event.childIndex, $event.age)"
      @remove-child="core.removeChild($event.roomIndex, $event.childIndex)"
      @add-room="core.addRoom"
      @remove-room="core.removeRoom"
      @close="core.close"
    >
      <template #reference>
        <div
          class="tourists-rooms-field__control"
          :class="{ 'tourists-rooms-field__control_active': core.controlActivated.value }"
          @click="onToggle"
        >
          <Typo
            class="tourists-rooms-field__label"
            :variant="TypoVariant.BodySRegular"
            tag="span"
          >{{ core.roomsLabel.value }}</Typo>

          <div class="tourists-rooms-field__value" data-testid="tourists-rooms-value">
            <Typo class="tourists-rooms-field__typo" :variant="TypoVariant.BodyLRegular" tag="span">{{ core.touristsValue.value }}</Typo>
          </div>
        </div>
      </template>
    </TouristsRoomsDropdown>
  </div>
</template>

<style scoped lang="scss">
  .tourists-rooms-field {
    position: relative;
    width: 100%;

    &__typo {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
    }

    &__control {
      background: var(--color-bg-primary);
      box-sizing: border-box;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxxs);
      height: 54px;
      justify-content: center;
      padding: var(--spacing-xs) var(--spacing-s);
      transition: background-color var(--transition-default);

      &_active {
        background: var(--color-disabled-100);
      }
    }

    &__label {
      color: var(--color-text-secondary);
      pointer-events: none;
      user-select: none;
    }

    &__value {
      color: var(--color-text-primary);
    }
  }
</style>
